import {
  AlertTriangle,
  TrendingDown,
  ShieldOff,
  FileWarning,
  Users,
  Landmark,
  Zap,
  Globe,
  TrendingUp,
  Shield,
  Truck,
  Package,
  ArrowRight,
  MessageCircle,
} from "lucide-react";
import { Link } from "react-router-dom";

const pairs = [
  {
    problem: {
      icon: TrendingDown,
      title: "Overpaying Suppliers",
      desc: "No visibility into real market prices",
    },
    solution: {
      icon: TrendingUp,
      title: "Market Intelligence",
      desc: "We know the market price — every purchase is cheaper from a vetted source",
    },
  },
  {
    problem: {
      icon: ShieldOff,
      title: "Vendor Fraud",
      desc: "Spec mismatches and unverified claims",
    },
    solution: {
      icon: Shield,
      title: "QA-Verified Goods",
      desc: "Every shipment inspected by our QA agents before dispatch",
    },
  },
  {
    problem: {
      icon: Landmark,
      title: "Unsafe Payment Methods",
      desc: "Risky advance payments",
    },
    solution: {
      icon: Zap,
      title: "Extended Payment Terms",
      desc: "Suppliers paid after QC inspection. Buyers pay Net 15 days to 60 days",
    },
  },
  {
    problem: {
      icon: FileWarning,
      title: "Logistics Blind Spots",
      desc: "No real-time shipment visibility",
    },
    solution: {
      icon: Truck,
      title: "In-House Logistics",
      desc: (
        <>
          AI Process automation of the whole process from PO to delivery along
          with real time track & trace of shipments and documents.{" "}
          {/* <a
            href="https://taram.io"
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary underline underline-offset-2 hover:text-primary/80 font-semibold"
          >
            Taram.io →
          </a> */}
        </>
      ),
    },
  },
  {
    problem: {
      icon: AlertTriangle,
      title: "Low Negotiating Power",
      desc: "Small volumes mean worse pricing",
    },
    solution: {
      icon: Package,
      title: "Bulk Buying Power",
      desc: "Pooled demand unlocks volume discounts and priority allocation",
    },
  },
  {
    problem: {
      icon: Users,
      title: "Scattered Ops",
      desc: "Fragmented teams across countries",
    },
    solution: {
      icon: Globe,
      title: "Global Offices",
      desc: "Documentation, compliance, and customs — all managed in-house",
    },
  },
];

const stats = [
  { value: "30+", label: "Countries served" },
  { value: "20%", label: "Avg. cost savings" },
];

export default function WhyTaramSection() {
  return (
    <section
      id="why"
      className="py-10 md:py-16 relative overflow-hidden bg-muted/30"
    >
      <div className="container px-4 relative">
        {/* Header */}
        <div className="mb-10">
          <p className="text-sm font-semibold text-primary mb-4 tracking-wide uppercase">
            Why Taram
          </p>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-display font-bold text-foreground leading-[1.1] tracking-tight max-w-xl">
              Not a broker.
              <br />
              <span className="text-gradient">Your procurement partner.</span>
            </h2>
            <div className="flex gap-8">
              {stats.map((s) => (
                <div key={s.value} className="text-center">
                  <p className="text-3xl md:text-4xl font-display font-bold text-foreground">
                    {s.value}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {s.label}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Two-column header labels */}
        <div className="hidden md:grid grid-cols-2 gap-6 mb-4 px-1">
          <p className="text-xs font-bold text-destructive/60 uppercase tracking-widest">
            The Problem
          </p>
          <p className="text-xs font-bold text-primary uppercase tracking-widest">
            Taram's Solution
          </p>
        </div>

        {/* Rows */}
        <div className="space-y-2">
          {pairs.map((pair) => (
            <div
              key={pair.problem.title}
              className="grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-6"
            >
              {/* Problem */}
              <div className="flex items-center gap-3 bg-background border border-border rounded-xl px-5 py-4">
                <pair.problem.icon className="w-5 h-5 text-destructive/50 shrink-0" />
                <div>
                  <p className="text-sm font-semibold text-foreground">
                    {pair.problem.title}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {pair.problem.desc}
                  </p>
                </div>
              </div>

              {/* Solution */}
              <div className="flex items-center gap-3 bg-primary/[0.04] border border-primary/15 rounded-xl px-5 py-4">
                <pair.solution.icon className="w-5 h-5 text-primary shrink-0" />
                <div>
                  <p className="text-sm font-semibold text-foreground">
                    {pair.solution.title}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {pair.solution.desc}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-8 flex flex-wrap gap-3 justify-center">
          <Link
            to={"/signup"}
            className="inline-flex items-center gap-2 px-6 py-3 text-sm font-bold rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
          >
            Get Started <ArrowRight className="w-4 h-4" />
          </Link>
          <a
            href="#offices"
            className="inline-flex items-center gap-2 px-6 py-3 text-sm font-bold rounded-lg border border-border text-foreground hover:bg-secondary transition-colors"
          >
            <MessageCircle className="w-4 h-4" /> Talk to Our Team
          </a>
        </div>
      </div>
    </section>
  );
}
